import { circle as turfCircle } from '@turf/circle';
import { pipe, assoc, mergeRight } from 'ramda';
import React from 'react';

import { Icon } from '@ui/icons/Icon.tsx';
import { findBoundsOfCoordinates } from '@utils/bounds.ts';
import { ordToFloor } from '@utils/geom.ts';

import { getDebugBoundsLayers } from './debugBoundsLayers.ts';
import { getNavGraphFeatureLayers } from './navGraphFeaturesLayers.ts';
import type {
  Coordinate,
  CornerBounds,
  GeoJsonMapFeature,
  GeoJsonProperties,
  LatLng,
  LineStringFeature,
  MapInitialized,
  MapRendererApp,
  MapWithDebugFlags,
  PointFeature,
  VenueMapData,
} from './types.ts';
import { createMapFeature, setSource, variousColors } from './utils.ts';

declare global {
  interface Window {
    _map?: MapWithDebugFlags;
    _poi?: unknown;
  }
}

type NavGraphNode = {
  id: string | number;
  lng: number;
  lat: number;
  ordinal: number | null;
  isOrphaned?: boolean;
};

type NavGraphEdge = {
  startCoordinates: Coordinate;
  endCoordinates: Coordinate;
  pathCoordinates?: Coordinate[];
  ordinal: number | null;
  category?: string;
  defaultStrokeColor?: string;
  isDriveway?: boolean;
};

type FeatureThemer = (feature: LineStringFeature<GeoJsonProperties>) => LineStringFeature<GeoJsonProperties>;

type StructureWithBounds = VenueMapData['structures'][number] & {
  bounds: { n: number; s: number; e: number; w: number };
};

type StructureWithBoundsPolygon = VenueMapData['structures'][number] & {
  boundsPolygon: number[][];
};

type FloorWithBoundsPolygon = {
  boundsPolygon: number[][];
};

type DebugState = {
  navGraphFeatures: {
    nodes: PointFeature<GeoJsonProperties>[];
    edges: LineStringFeature<GeoJsonProperties>[];
  };
  ordinal: number | null;
};

const isDefined = <T,>(value: T | null): value is T => value !== null;
const hasBounds = (structure: VenueMapData['structures'][number]): structure is StructureWithBounds =>
  Boolean(structure.bounds);
const hasBoundsPolygon = (structure: VenueMapData['structures'][number]): structure is StructureWithBoundsPolygon =>
  Boolean(structure.boundsPolygon);
const hasFloorBoundsPolygon = (floor: unknown): floor is FloorWithBoundsPolygon =>
  Boolean(
    floor &&
    typeof floor === 'object' &&
    'boundsPolygon' in floor &&
    Array.isArray((floor as FloorWithBoundsPolygon).boundsPolygon),
  );

export default function DebugToolsController(app: MapRendererApp, mapInitialized: MapInitialized) {
  const SOURCE_NAVGRAPH_NODES = 'llDebugGraphNodesSource';
  const SOURCE_NAVGRAPH_EDGES = 'llDebugGraphEdgesSource';
  const SOURCE_DEBUG_BOUNDS = 'llDebugBoundsSource';

  let state: DebugState = {
    navGraphFeatures: {
      nodes: [],
      edges: [],
    },
    ordinal: null,
  };

  const featureThemer = app.bus.get('map/getMapFeatureThemer');

  exposeMapState();

  function exposeMapState() {
    mapInitialized().then(map => {
      window._map = map;
    });
    app.bus.monitor('map/poiClicked', payload => {
      window._poi = (payload as { poi?: unknown }).poi;
    });
  }

  const initMapSources = pipe(
    assoc(SOURCE_NAVGRAPH_EDGES, createMapFeature([])),
    assoc(SOURCE_NAVGRAPH_NODES, createMapFeature([])),
    assoc(SOURCE_DEBUG_BOUNDS, createMapFeature([])),
  );

  const initMapLayers = (layers: unknown[]) =>
    layers
      .concat(getNavGraphFeatureLayers(SOURCE_NAVGRAPH_NODES, SOURCE_NAVGRAPH_EDGES))
      .concat(getDebugBoundsLayers(SOURCE_DEBUG_BOUNDS));

  app.bus.on('map/showOrphanedGraphNodes', ({ orphanedNodes }: { orphanedNodes: NavGraphNode[] }) =>
    orphanedNodes.forEach((n, index) =>
      app.bus.send('map/addMarker', {
        id: `orphan-${index}`,
        lat: n.lat,
        lng: n.lng,
        ordinal: n.ordinal,
        markerComponent: <Icon cdn name="dot.eat" width={12} height={12} />,
      }),
    ),
  );

  app.bus.on(
    'map/showNavGraphFeatures',
    ({ navGraph }: { navGraph: { nodes: NavGraphNode[]; edges: NavGraphEdge[] } }) =>
      mapInitialized().then(async map => {
        const themer = (await featureThemer) as FeatureThemer;
        const nodes = navGraph.nodes.map(toNodeGeoJsonFeature);
        const edges = navGraph.edges.map(
          edge => themer(toEdgeGeoJsonFeature(edge)) as LineStringFeature<GeoJsonProperties>,
        );
        state.navGraphFeatures = { nodes, edges };
        filterFeaturesOnOrdinal(map);
        app.bus.send('map/highlightEntities', { ids: ['0'] });
      }),
  );

  app.bus.on(
    'map/showVenueBounds',
    async ({
      venueCenter,
      venueRadius,
      bounds,
    }: {
      venueCenter: LatLng;
      venueRadius: number;
      bounds: CornerBounds;
    }) => {
      const mapCenterFeature = toPointGeoJsonFeature(venueCenter);

      const circle = turfCircle([venueCenter.lng, venueCenter.lat], venueRadius / 1000);
      const circleFeature = toLineStringGeoJson(circle.geometry.coordinates[0] as Coordinate[], {});
      const boundFeatures = toSquare(bounds);
      mapInitialized().then(map =>
        setSource(SOURCE_DEBUG_BOUNDS, [mapCenterFeature, circleFeature, boundFeatures], map),
      );
    },
  );

  const latLngSwap = (point: number[]): Coordinate => [point[1], point[0]];
  const vertsLatLngSwap = (vertsArray: number[][]): Coordinate[] => vertsArray.map(latLngSwap);

  let colorIndex = 0;
  const nextColor = () => variousColors[colorIndex++ % variousColors.length];

  app.bus.on('map/showBuildingBounds', async ({ nameFilter }: { nameFilter?: string }) => {
    const venueData = (await app.bus.get('venueData/getVenueData')) as VenueMapData;

    const boundingBoxFeatures = venueData.structures
      .filter(s => (nameFilter ? s.name === nameFilter : true))
      .filter(hasBounds)
      .map(s => toSquare2(s.bounds, { color: nextColor() }));

    const polyOutlineFeatures = venueData.structures
      .filter(s => (nameFilter ? s.name === nameFilter : true))
      .filter(hasBoundsPolygon)
      .map(s =>
        toLineStringGeoJson(vertsLatLngSwap(s.boundsPolygon), {
          color: nextColor(),
          width: 2,
        }),
      );

    const buildingFeatures = boundingBoxFeatures.concat(polyOutlineFeatures);

    mapInitialized().then(map => setSource(SOURCE_DEBUG_BOUNDS, buildingFeatures, map));
  });

  app.bus.on('map/showFloorBounds', async ({ nameFilter: buildingName }: { nameFilter?: string }) => {
    const venueData = (await app.bus.get('venueData/getVenueData')) as VenueMapData;
    const ordinal = (await app.bus.get('map/getCurrentOrdinal')) as number | null;

    if (ordinal === null) {
      return;
    }

    const floorBoxFeatures = venueData.structures
      .filter(s => (buildingName ? s.name === buildingName : true))
      .map(s => {
        const floor = ordToFloor(s, ordinal);
        if (hasFloorBoundsPolygon(floor)) {
          const bounds = findBoundsOfCoordinates(floor.boundsPolygon);
          return toSquare2(bounds, { color: nextColor() });
        }
        return null;
      })
      .filter(isDefined);

    const polyOutlineFeatures = venueData.structures
      .filter(s => (buildingName ? s.name === buildingName : true))
      .map(s => {
        const floor = ordToFloor(s, ordinal);
        return hasFloorBoundsPolygon(floor)
          ? toLineStringGeoJson(vertsLatLngSwap(floor.boundsPolygon), {
              color: nextColor(),
              width: 2,
            })
          : null;
      })
      .filter(isDefined);

    const floorFeatures = floorBoxFeatures.concat(polyOutlineFeatures);

    mapInitialized().then(map => setSource(SOURCE_DEBUG_BOUNDS, floorFeatures, map));
  });

  const toNodeGeoJsonFeature = (node: NavGraphNode): PointFeature<GeoJsonProperties> => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [node.lng, node.lat],
    },
    properties: {
      nodeId: node.id,
      ordinal: node.ordinal,
      isOrphaned: node.isOrphaned,
    },
  });

  // Convert bounds { ne: {lat,lng}, sw: { lat,lng} } to a feature
  const toSquare = (bounds: CornerBounds): LineStringFeature<GeoJsonProperties> => {
    const boundsRectangle: Coordinate[] = [
      [bounds.ne.lng, bounds.ne.lat],
      [bounds.sw.lng, bounds.ne.lat],
      [bounds.sw.lng, bounds.sw.lat],
      [bounds.ne.lng, bounds.sw.lat],
      [bounds.ne.lng, bounds.ne.lat],
    ];
    return toLineStringGeoJson(boundsRectangle);
  };

  // Same as above, but treat bounds as { n, s, e, w}
  const toSquare2 = (
    bounds: { n: number; s: number; e: number; w: number },
    properties?: GeoJsonProperties,
  ): LineStringFeature<GeoJsonProperties> => {
    const boundsRectangle: Coordinate[] = [
      [bounds.e, bounds.n],
      [bounds.w, bounds.n],
      [bounds.w, bounds.s],
      [bounds.e, bounds.s],
      [bounds.e, bounds.n],
    ];
    return toLineStringGeoJson(boundsRectangle, properties);
  };

  const toEdgeGeoJsonFeature = (edge: NavGraphEdge): LineStringFeature<GeoJsonProperties> => ({
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: edge.pathCoordinates?.length ? edge.pathCoordinates : [edge.startCoordinates, edge.endCoordinates],
    },
    properties: {
      ordinal: edge.ordinal,
      category: edge.category,
      strokeColor: edge.defaultStrokeColor,
      isDriveway: edge.isDriveway,
    },
  });

  const toPointGeoJsonFeature = (location: LatLng): PointFeature<GeoJsonProperties> => {
    const feature = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [location.lng, location.lat],
      },
    } as PointFeature<GeoJsonProperties>;
    return feature;
  };

  const toLineStringGeoJson = (
    coordinates: Coordinate[],
    properties?: GeoJsonProperties,
  ): LineStringFeature<GeoJsonProperties> => {
    const feature = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates,
      },
    } as LineStringFeature<GeoJsonProperties>;
    if (properties) {
      feature.properties = properties;
    }
    return feature;
  };

  const clearNavGraphState = () =>
    mapInitialized().then(map => {
      state.navGraphFeatures = { nodes: [], edges: [] };
      setSource(SOURCE_NAVGRAPH_NODES, [], map);
      setSource(SOURCE_NAVGRAPH_EDGES, [], map);
    });
  app.bus.on('map/resetNavGraphFeatures', clearNavGraphState);

  const clearBoundsSource = () => mapInitialized().then(map => setSource(SOURCE_DEBUG_BOUNDS, [], map));
  app.bus.on('map/resetVenueBounds', clearBoundsSource);

  app.bus.on('map/cleanMap', () => {
    clearNavGraphState();
    clearBoundsSource();
  });

  app.bus.on('map/ordinalChanged', ({ ordinal }: { ordinal: number | null }) =>
    mapInitialized().then(map => {
      state.ordinal = ordinal;
      filterFeaturesOnOrdinal(map);
    }),
  );

  const filterFeaturesOnOrdinal = (map: MapWithDebugFlags) => {
    const matchesCurrentOrdinal = (feature: GeoJsonMapFeature) => feature.properties?.ordinal === state.ordinal;
    const nodeFeatures = state.navGraphFeatures.nodes.filter(matchesCurrentOrdinal);
    const edgeFeatures = state.navGraphFeatures.edges.filter(matchesCurrentOrdinal);
    setSource(SOURCE_NAVGRAPH_NODES, nodeFeatures, map);
    setSource(SOURCE_NAVGRAPH_EDGES, edgeFeatures, map);
  };

  app.bus.on('map/togglePadding', () => {
    mapInitialized().then(map => {
      (map as MapWithDebugFlags).showPadding = !(map as MapWithDebugFlags).showPadding;
    });
  });

  const runTest = async (initialState: Partial<DebugState>, testRoutine: () => Promise<void> | void) => {
    state = mergeRight(state, initialState);
    await testRoutine();
    return state;
  };

  return { initMapLayers, initMapSources, runTest };
}
